import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { AlertTriangle, CheckCircle2, Inbox, X } from "lucide-react";

/**
 * Feedback primitives: toasts, confirmation, empty and error states.
 *
 * The panel had none of these. Destructive actions used `window.confirm`,
 * successes were invisible, and failures were invisible *and* silently
 * discarded — a denied write left the row looking updated until reload.
 */

// ── Toasts ──────────────────────────────────────────────────────────────────

type ToastKind = "success" | "error";
type Toast = { id: number; kind: ToastKind; message: string };

type ToastValue = {
  success: (message: string) => void;
  /** Accepts an Error so call sites can pass a caught value straight through. */
  failure: (error: unknown) => void;
};

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, kind, message }]);
    // Errors linger — they usually say something the user needs to read and
    // act on, where a success is just an acknowledgement.
    window.setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      kind === "error" ? 8000 : 3500
    );
  }, []);

  const value = useMemo<ToastValue>(
    () => ({
      success: (message) => push("success", message),
      failure: (error) =>
        push(
          "error",
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Something went wrong"
        ),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 400,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxWidth: 380,
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "13px 15px",
              borderRadius: 14,
              background: "#fff",
              border: `1px solid ${toast.kind === "error" ? "var(--danger-light)" : "var(--success-light)"}`,
              borderLeft: `4px solid ${toast.kind === "error" ? "var(--danger)" : "var(--success)"}`,
              boxShadow: "var(--shadow-md)",
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--text-primary)",
            }}
          >
            {toast.kind === "error" ? (
              <AlertTriangle size={16} color="var(--danger)" style={{ flexShrink: 0, marginTop: 1 }} />
            ) : (
              <CheckCircle2 size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
            )}
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              style={{ background: "none", border: "none", padding: 0, color: "var(--text-muted)" }}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
}

// ── Confirmation ────────────────────────────────────────────────────────────

/**
 * A real dialog rather than `window.confirm`.
 *
 * The difference that matters isn't cosmetic: this one can require a typed
 * reason, and several of the `admin_*` functions reject the call without one.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  destructive,
  requireReason,
  reasonLabel = "Reason",
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  requireReason?: boolean;
  reasonLabel?: string;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");

  // Reset whenever the dialog is opened for a new subject, so a reason typed
  // for one user can't be submitted against the next.
  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  const armed = !requireReason || reason.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24, 27, 46, 0.42)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 500,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-md)",
          padding: 24,
          width: "100%",
          maxWidth: 440,
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" }}>{title}</div>
        <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginTop: 8 }}>
          {body}
        </div>

        {requireReason && (
          <div style={{ marginTop: 18 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--text-muted)",
                marginBottom: 6,
              }}
            >
              {reasonLabel}
            </label>
            <textarea
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              style={{ resize: "vertical", minHeight: 72 }}
              placeholder="Recorded in the audit log and shown to the user"
              autoFocus
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 22 }}>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn"
            onClick={() => onConfirm(reason.trim())}
            disabled={!armed || busy}
            style={{
              background: destructive ? "var(--danger)" : "var(--primary)",
              borderColor: "transparent",
              color: "#fff",
              opacity: !armed || busy ? 0.55 : 1,
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div style={{ textAlign: "center", padding: "56px 20px", color: "var(--text-muted)" }}>
      <Inbox size={26} style={{ opacity: 0.5 }} />
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-secondary)", marginTop: 12 }}>
        {message}
      </div>
      {hint && <div style={{ fontSize: 12.5, marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

/**
 * Shown when a request fails.
 *
 * Says what went wrong rather than rendering an empty table — with RLS in
 * front of everything, "permission denied for table profiles" is the single
 * most useful string this panel can show you, and it used to be swallowed.
 */
export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px" }}>
      <AlertTriangle size={26} color="var(--danger)" />
      <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", marginTop: 12 }}>
        Couldn&apos;t load this
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "var(--text-muted)",
          marginTop: 6,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          wordBreak: "break-word",
          maxWidth: 460,
          margin: "6px auto 0",
        }}
      >
        {error}
      </div>
      {onRetry && (
        <button className="btn" style={{ marginTop: 16 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Row-shaped placeholders. Keeps table height stable between loads. */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c}>
              <div
                style={{
                  height: 11,
                  borderRadius: 6,
                  background: "linear-gradient(90deg, #eef0f6, #f7f8fc, #eef0f6)",
                  backgroundSize: "200% 100%",
                  animation: "adminShimmer 1.3s ease-in-out infinite",
                  width: c === 0 ? "70%" : `${45 + ((r + c) % 3) * 15}%`,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
